import React from 'react';

import { type ChakraProviderProps, Flex } from '@chakra-ui/react';

import { NestedChakraProvider } from '../../providers';
import { Spinner, type SpinnerProps } from '../spinner';
import { Text } from '../text';

export type SpinnerOverlayProps = {
  /**
   * Theme to render the overlay with. Pass it when the overlay renders
   * before/outside the page's Chakra provider (early returns, auth guards) —
   * without a theme above it the spinner collapses to a few pixels. Omit it
   * when a provider is already mounted above.
   */
  theme?: ChakraProviderProps['theme'];
  /** Fully opaque background instead of the default translucent scrim */
  solidBackground?: boolean;
  /** Optional text under the spinner */
  label?: string;
  size?: SpinnerProps['size'];
};

/**
 * Fullscreen loading overlay. Render it conditionally while loading:
 * `if (isLoading) return <SpinnerOverlay theme={theme} />;`
 */
export const SpinnerOverlay = ({
  theme,
  solidBackground = false,
  label,
  size,
}: SpinnerOverlayProps) => {
  const overlay = (
    <Flex
      role="status"
      position="fixed"
      inset={0}
      zIndex="overlay"
      align="center"
      justify="center"
      bg={solidBackground ? 'neutral.bg-primary' : 'whiteAlpha.800'}
      _dark={{
        bg: solidBackground ? 'neutral.bg-primary' : 'blackAlpha.800',
      }}
    >
      <Flex direction="column" align="center" gap={2}>
        <Spinner size={size} _dark={{ color: 'white' }} />
        {label && <Text color="neutral.fg-secondary">{label}</Text>}
      </Flex>
    </Flex>
  );

  return theme ? (
    <NestedChakraProvider theme={theme}>{overlay}</NestedChakraProvider>
  ) : (
    overlay
  );
};
